// `HardBlockRedirect` — root-level guard that redirects to
// `/billing-locked` once the subscription has been in soft-block for
// >30 days (US-200, hard-block).
//
// Wave B6 changed the redirect target from `/settings/billing` to a
// dedicated splash route (`/billing-locked`) so the lock has its own
// acknowledgement step before dropping the user into the settings tree.
// The user can still reach `/settings/billing` from the splash CTA.
//
// Allowed paths: the splash itself, the billing settings section, GDPR
// export, and any auth route.

import { useEffect } from 'react';

import { useNavigate, useRouterState } from '@tanstack/react-router';

import { deriveTrialState } from './TrialStateMachine';
import { useSubscription } from './useSubscription';

const ALLOWED_PATHS = [
  /^\/billing-locked/,
  /^\/settings\/billing/,
  /^\/api\/v1\/me\/export/,
  /^\/auth\//,
];

export interface HardBlockRedirectProps {
  /** Override allowed paths (tests). */
  allowedPaths?: RegExp[];
}

export function HardBlockRedirect({ allowedPaths = ALLOWED_PATHS }: HardBlockRedirectProps) {
  const navigate = useNavigate();
  const router = useRouterState();
  const query = useSubscription();
  const state = deriveTrialState(query.data);

  useEffect(() => {
    if (state.accessTier !== 'hard_block') return;
    const path = router.location.pathname;
    if (allowedPaths.some((re) => re.test(path))) return;
    void navigate({ to: '/billing-locked' });
  }, [state.accessTier, router.location.pathname, navigate, allowedPaths]);

  return null;
}
